// What the phone's screens share (Poulpe-624), as the approved prototype
// drew it (mobile/shared.tsx): the top bar, with the logo on every screen
// and a labelled way back where a screen has one; the offline banner;
// the tab bar; and how a screen names an issue and who asks.
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { Actor, Issue, Question, Session } from '../api/types'
import { BoardLogo, BoardWordmark, ChevronLeftIcon } from '../components/Icons'
import { useBoardState, useStore } from '../store/context'
import { actorName } from '../vocab'
import { chooseFullBoard } from './usePhone'

/** The logo, as the signed-out header draws it (App.tsx): the mark and the word. */
export function Brand({ size = 22 }: { size?: number }) {
  return (
    <span className="brand" aria-label="Poulpe" role="img">
      <BoardLogo size={size} />
      <span className="brand-name"><BoardWordmark height={Math.round(size * 0.8)} /></span>
    </span>
  )
}

/**
 * A screen's top: the logo on every screen (Poulpe-880), and on a screen a
 * person returns to, the account menu beside it; elsewhere the way back,
 * always labelled, with the logo after it, as the desktop's header pairs its
 * history buttons and logo. Then its title, if it has one: loading and an
 * error have none.
 */
export function TopBar({ title, back = '/', backLabel = 'Needs you', brand = false }: { title?: string; back?: string | null; backLabel?: string; brand?: boolean }) {
  return (
    <header className="app-header m-top">
      <div className="m-top-row">
        {!brand && back && (
          <Link className="m-back m-target" to={back} aria-label={`Back to ${backLabel}`}>
            <ChevronLeftIcon size={16} /> {backLabel}
          </Link>
        )}
        <Brand />
        {brand && <AccountMenu />}
      </div>
      {title && <h1 className="m-title">{title}</h1>}
    </header>
  )
}

/**
 * Who is signed in, and where; Sign out, which a shared phone needs; and the
 * full board, for what the phone's screens do not show. A disclosure, so it
 * needs no script to open and is reached as a button.
 */
export function AccountMenu() {
  const { me } = useBoardState()
  const store = useStore()
  if (!me?.member) return null
  return (
    <details className="m-menu">
      <summary className="m-target" aria-label={`Your account: ${me.member.name}`}>{me.member.name}</summary>
      <div className="m-menu-body">
        <p className="muted">Signed in as {me.member.name} · {me.workspace.name}</p>
        <button type="button" className="button m-target" onClick={() => chooseFullBoard(true)}>Full board</button>
        <button type="button" className="button m-target" onClick={() => void store.signOut()}>Sign out</button>
      </div>
    </details>
  )
}

/** Offline: what shows is what the phone kept, and a way to try the connection again. */
export function OfflineBanner() {
  const { offline } = useBoardState()
  const store = useStore()
  if (!offline) return null
  return (
    <div className="question-changed m-banner" role="status">
      <p>Offline · showing what this phone kept. An answer waits on this phone while Poulpe stays open, and goes once it is back online; approvals need a connection.</p>
      <button type="button" className="button m-target" onClick={() => void store.catchUp()}>
        Retry connection
      </button>
    </div>
  )
}

/** The phone's tabs. */
export type Tab = 'needs' | 'board' | 'digest'

/** The tab bar: Needs you, with the header's badge, the board (Poulpe-881) and the digest. */
export function TabBar({ current, count }: { current: Tab | null; count: number }) {
  return (
    <nav className="app-nav m-tabs" aria-label="Sections">
      <Link className={current === 'needs' ? 'active' : undefined} to="/" aria-current={current === 'needs' ? 'page' : undefined}>
        Needs you {count > 0 && <span className="nav-badge" aria-label={`${count} waiting`}>{count}</span>}
      </Link>
      <Link className={current === 'board' ? 'active' : undefined} to="/board" aria-current={current === 'board' ? 'page' : undefined}>
        Board
      </Link>
      <Link className={current === 'digest' ? 'active' : undefined} to="/digest" aria-current={current === 'digest' ? 'page' : undefined}>
        Digest
      </Link>
    </nav>
  )
}

/** A screen: its top, the offline banner, its body that scrolls, and an optional foot and tab bar. */
export function Screen({ top, children, foot, tabs }: { top: ReactNode; children: ReactNode; foot?: ReactNode; tabs?: { current: Tab | null; count: number } }) {
  return (
    <div className="m-screen">
      {top}
      <OfflineBanner />
      <main className="m-body">{children}</main>
      {foot && <div className="m-foot">{foot}</div>}
      {tabs && <TabBar {...tabs} />}
    </div>
  )
}

/** An issue, as the desktop's question card names it (questions/QuestionCard.tsx): its key and its title. */
export function IssueName({ issue }: { issue: Issue }) {
  return (
    <span className="request-issue">
      <span className="request-issue-key">{issue.key}</span>
      <span className="request-issue-title">{issue.title}</span>
    </span>
  )
}

/** Who asked, by the session's agent. */
export function askerName(session: Session | null | undefined): string {
  if (!session) return 'An agent'
  const actor: Actor = { kind: 'agent', agent: session.agent, session: session.id }
  return actorName(actor)
}

/** A question's first line, plain, for a list. */
export function headline(question: Question): string {
  const text = question.prompts[0]?.text ?? ''
  return text.split('\n').find((line) => line.trim())?.trim() ?? 'A question'
}

export function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

/** A short state for a screen's body: a note, in the app's muted words. */
export function Note({ children, tone = 'muted', role = 'status' }: { children: ReactNode; tone?: string; role?: 'status' | 'alert' }) {
  return <p className={`request-note ${tone}`} role={role}>{children}</p>
}
